export function getPath(
  source: Record<string, unknown> | null | undefined,
  path: string
): unknown {
  if (!source) return undefined;
  let current: unknown = source;
  for (const segment of path.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

export function hasPath(
  source: Record<string, unknown> | null | undefined,
  path: string
): boolean {
  return getPath(source, path) !== undefined;
}

export function setPath(
  source: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const clone = structuredClone(source);
  const segments = path.split(".");
  let current = clone;
  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const child = current[segment];
    if (!child || typeof child !== "object" || Array.isArray(child)) {
      current[segment] = {};
    }
    current = current[segment] as Record<string, unknown>;
  }
  current[segments.at(-1)!] = value;
  return clone;
}

export function deletePath(
  source: Record<string, unknown>,
  path: string
): Record<string, unknown> {
  const clone = structuredClone(source);
  const segments = path.split(".");
  const stack: Array<[Record<string, unknown>, string]> = [];
  let current = clone;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const child = current[segment];
    if (!child || typeof child !== "object" || Array.isArray(child)) {
      return clone;
    }
    stack.push([current, segment]);
    current = child as Record<string, unknown>;
  }

  delete current[segments.at(-1)!];

  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const [parent, key] = stack[index];
    const value = parent[key];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value as Record<string, unknown>).length === 0
    ) {
      delete parent[key];
    }
  }

  return clone;
}

export function minutesToTime(minutes: number): string {
  const normalized = Math.max(0, Math.min(1439, minutes));
  const hours = Math.floor(normalized / 60);
  const rest = normalized % 60;
  return `${hours.toString().padStart(2, "0")}:${rest
    .toString()
    .padStart(2, "0")}`;
}

export function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return Math.max(0, Math.min(1439, hours * 60 + minutes));
}

export function formatDate(value: string | null, language: "ar" | "en"): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "ar" ? "ar-AE" : "en-AE", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}
